import Papa from 'papaparse';
import { useState } from 'react';

export const MAX_FILE_SIZE = 30 * 1_000_000; // 30MB;
export const NO_FILE_ERROR = 'Choose a file to upload';
export const FILE_TOO_LARGE_ERROR =
  'The file is too large to be uploaded. Files larger than 30MB are not currently supported.';

export type UseParseCSVFileParams = {
  validateHeaders: (headers: string[]) => {
    isValid: boolean;
    error?: string;
  };
};
export const useParseCSVFile = ({ validateHeaders }: UseParseCSVFileParams) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [output, setOutput] = useState<unknown[] | null>(null);
  const [headers, setHeaders] = useState<string[] | null>(null);

  const handleReset = () => {
    setError(null);
    setSelectedFile(null);
    setOutput(null);
    setHeaders(null);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleReset();
    const file = event.target.files?.[0] || null;

    if (!file) {
      setError(NO_FILE_ERROR);
      return;
    }

    setSelectedFile(file);

    if (file.size > MAX_FILE_SIZE) {
      setError(FILE_TOO_LARGE_ERROR);
      return;
    }

    // parse with papaparse
    Papa.parse(file, {
      header: true,
      complete: result => {
        const fileHeaders = result.meta.fields ?? [];
        const { isValid, error } = validateHeaders(fileHeaders);
        if (isValid) {
          setOutput(result.data);
          setHeaders(fileHeaders);
        } else if (error) {
          setError(error);
        }
      },
      error: error => {
        setError(`Error parsing file ${error}`);
      },
    });
  };

  return {
    headers,
    selectedFile,
    handleFileInputChange,
    output,
    error,
    handleReset,
  };
};
